import "react-medium-image-zoom/dist/styles.css";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Container, ListGroup, Button, Form } from "react-bootstrap";
import {
  ReviewsList,
  ReviewForm,
  ModalMessage,
  LoadingSpinner,
  ImagesWithPreview,
} from "../../components";
import { Rating } from "react-simple-star-rating";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../redux/cartSlice.js";
import { fetchProductApi } from "../../api/productsApi";
import { fetchProductReviewsApi } from "../../api/reviewsApi";
import { addToStorageCart } from "../../utilities";

export default function ProductDetails() {
  // displays product details with images, ratings, price, description, etc
  // option to add item to the cart
  // option to leave a review for this product

  const dispatch = useDispatch();
  const { id } = useParams();
  const stayLoggedIn = useSelector((state) => state.user.stayLoggedIn);
  const isAdmin = useSelector((state) => state.user.isAdmin);
  const reviewsRef = useRef(null); 

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [cartQuantity, setCartQuantity] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [updateReviews, setUpdateReviews] = useState(true);

  useEffect(() => {
    const productsAbortController = new AbortController();
    const reviewsAbortController = new AbortController();

    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const res = await fetchProductApi(id, productsAbortController.signal);
        return res;
      } catch (error) {
        setErrorMsg("Could not load product data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const res = await fetchProductReviewsApi(
          id,
          reviewsAbortController.signal
        );
        return res;
      } catch (error) {
        setErrorMsg("Could not load reviews data. Please try again later.");
      }
    };

    setUpdateReviews(false);
    fetchProduct()
      .then((res) => {
        setProduct(res);
      })
      .catch((error) =>
        setErrorMsg("Could not load product data. Please try again later.")
      );

    fetchReviews()
      .then((res) => {
        setReviews(res);
      })
      .catch((error) =>
        setErrorMsg("Could not load reviews data. Please try again later.")
      );
    return () => {
      productsAbortController.abort();
      reviewsAbortController.abort();
    };
  }, [updateReviews, id]);

  const handleAddToCart = () => {
    // dispatch cart to store and update storage
    try {
      dispatch(addToCart({ productId: id, count: Number(cartQuantity) }));
      addToStorageCart({ productId: id, count: cartQuantity }, stayLoggedIn);
      setSuccessMsg("Item(s) added to the cart!");
    } catch (error) {
      setErrorMsg("Could not add items to the cart. Please try again later.");
    }
  };

  const scrollToReviews = () => {
    if (reviewsRef.current) {
      reviewsRef.current.scrollIntoView({ behaviour: "smooth" });
    }
  };

  return (
    <Container className="mt-3">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          // linkPath="/cart"
          // buttonText="Go To Cart"
          setSuccessMsg={setSuccessMsg}
        />
      )}
      {isLoading && <LoadingSpinner />}
      {product && (
        <>
          <Row className="mt-5">
            {/* IMAGES : 1 big main image, small ones in preview on left*/}
            <Col lg={6}>
              <ImagesWithPreview images={product.images} />
            </Col>

            {/* DETAILS */}
            <Col lg={6}>
              <ListGroup variant="flush">
                <ListGroup.Item className="border-0">
                  <h3>{product?.name}</h3>
                <div onClick={scrollToReviews} className="pointer">
                  <Rating
                    readonly
                    size={20}
                    initialValue={product?.rating}
                    allowFraction={true}
                    className="mb-2 me-1"
                  />
                  {product?.rating !== 0 && (
                    <span className="me-1">{product?.rating.toFixed(1)}</span>
                  )}
                  <span>
                    ({product?.nrOfReviews}{" "}
                    {product?.nrOfReviews === 1 ? " Review" : " Reviews"})
                  </span>
                </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <span
                    className="price"
                    style={{ fontSize: "xx-large", fontWeight: "500" }}
                  >
                    {product.price.toLocaleString("en-CA", {
                      style: "currency",
                      currency: "CAD",
                    })}
                  </span>
                </ListGroup.Item>
                <ListGroup.Item className="text-block">{product.description}</ListGroup.Item>
              </ListGroup>

              {/* ATTRIBUTES */}
              {product?.attributes?.length > 0 && (
                <Row style={{ padding: "16px" }}>
                  <Col>
                    {product.attributes?.map((attr, idx) => {
                      const keyVal = attr.name.split(":"); // = [key, value]
                      return (
                        <span key={attr._id} style={{ marginRight: "15px" }}>
                          {"#" + keyVal[1]}
                        </span>
                      );
                    })}
                  </Col>
                </Row>
              )}
              <Row className="mt-5">
                <Col>
                  {" "}
                  <ListGroup variant="flush">
                    <ListGroup.Item className="border-0">
                      <b>Status: </b>{" "}
                      {product?.inventoryCount > 0 ? (
                        <i
                          style={{ color: "green" }}
                          className="bi bi-check2-circle"
                        >
                          {" "}
                          In stock
                        </i>
                      ) : (
                        <i
                          style={{ color: "red" }}
                          className="bi bi-exclamation-circle"
                        >
                          {" "}
                          Sold out
                        </i>
                      )}
                    </ListGroup.Item>
                    {product.inventoryCount > 0 && (
                      <ListGroup.Item className="border-0">
                        Quantity
                        <Form.Select
                          size="md"
                          aria-label="Default select example"
                          onChange={(event) =>
                            setCartQuantity(Number(event.target.value))
                          }
                          disabled={product.inventoryCount < 1}
                        >
                          {Array.from(
                            { length: product?.inventoryCount },
                            (_, idx) => idx + 1
                          ).map((nr) => (
                            <option key={nr} value={nr}>
                              {nr}
                            </option>
                          ))}
                        </Form.Select>
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item>
                      <Button
                        onClick={handleAddToCart}
                        variant="primary"
                        disabled={isAdmin || product.inventoryCount < 1}
                      >
                        Add To Cart
                      </Button>
                      {isAdmin && <p style={{color: "#a50202", fontSize: "smaller", marginTop: "5px"}}>Admins cannot purchase items.</p>}
                    </ListGroup.Item>
                  </ListGroup>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* REVIEWS */}
          <h5 ref={reviewsRef} className="mt-5 mb-2">CUSTOMER REVIEWS</h5>
          <div>
            <span
              className="me-1"
              style={{ fontSize: "27px", fontWeight: "bold" }}
            >
              {product?.rating !== 0 && product?.rating.toFixed(1)}
            </span>
            <Rating
              readonly
              size={27}
              initialValue={product?.rating}
              allowFraction={true}
              style={{paddingBottom: "0.8rem", marginLeft: "5px"}}
            />
          </div>

          {reviews && reviews.length > 0 && (
            <span>
              Average rating based on {product?.nrOfReviews}{" "}
              {product?.nrOfReviews === 1 ? " review" : " reviews"}
            </span>
          )}

          {reviews && reviews.length > 0 ? (
            <Row>
              <ReviewsList reviews={reviews} />
              <h5 className="mt-5">Leave A Review</h5>
            </Row>
          ) : (
            <p>No reviews, yet. Be the first to review this product!</p>
          )}
          <Row>
            <ReviewForm
              productId={id}
              productName={product?.name}
              setSuccessMsg={setSuccessMsg}
              setErrorMsg={setErrorMsg}
              setUpdateReviews={setUpdateReviews}
            />
          </Row>
        </>
      )}
    </Container>
  );
}
